import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Chrome,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/common/Logo';
import './Auth.css';

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const { state, login, loginWithGoogle, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (state.isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [state.isAuthenticated, navigate, location]);

  // Clear errors when component mounts
  useEffect(() => {
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (state.error) {
      clearError();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(formData.email, formData.password);
  };

  const handleGoogleLogin = async () => {
    await loginWithGoogle();
  };

  return (
    <div className="auth-page fixed inset-0 overflow-y-auto overscroll-contain">
      <div className="auth-background" aria-hidden="true">
        <div className="auth-background__grid" />
        <div className="auth-background__glow auth-background__glow--top" />
        <div className="auth-background__glow auth-background__glow--bottom" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20">
          <div className="auth-header flex items-center justify-between">
            <Logo size="lg" />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/signup');
              }}
              className="auth-header__action"
            >
              Sign Up
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="auth-main flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-md">
            {/* Login Form */}
            <div className="auth-card">
              <div className="auth-card__intro">
                <span className="auth-card__eyebrow">Welcome back</span>
                <h1>Sign in to continue</h1>
                <p>Resume your learning paths, practice sessions, and saved progress.</p>
              </div>

              {/* Error Message */}
              {state.error && (
                <div className="auth-alert">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-orange-500" />
                    <p className="text-orange-200 text-sm">{state.error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" name="login" autoComplete="on">
                {/* Email Field */}
                <div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    autoComplete="username"
                    className="auth-input"
                    placeholder="Email address"
                    required
                  />
                </div>

                {/* Password Field */}
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="current-password"
                    className="auth-input auth-input--password"
                    placeholder="Password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="auth-password-toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Sign In Button */}
                <button
                  type="submit"
                  disabled={state.isLoading}
                  className="auth-primary-button"
                >
                  {state.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Signing In...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>

                {/* Remember Me */}
                <label className="auth-check-row">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className="auth-checkbox"
                  />
                  <span>Remember me</span>
                </label>
              </form>

              {/* OR Divider */}
              <div className="auth-divider">
                <span>or</span>
              </div>

              {/* SSO Options */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={state.isLoading}
                className="auth-secondary-button"
              >
                <Chrome className="w-5 h-5" />
                <span>{state.isLoading ? 'Signing in...' : 'Continue with Google'}</span>
              </button>

              {/* Help */}
              <div className="auth-help-link">
                <Link
                  to="/forgot-password"
                  className="auth-link"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Sign Up */}
              <div className="auth-switch-copy">
                <p>
                  New to LetsBoostUp?{' '}
                  <button
                    type="button"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      navigate('/signup');
                    }}
                    className="auth-link auth-link--strong"
                  >
                    Sign up now
                  </button>
                  .
                </p>
              </div>

              {/* Fine Print */}
              <div className="auth-fine-print">
                <p>
                  This page is protected by Google reCAPTCHA to ensure you're not a bot.{' '}
                  <button type="button" onClick={() => window.open('https://www.google.com/recaptcha/about/', '_blank')} className="auth-link">Learn more</button>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
